import React, { Component } from 'react';
import * as _ from 'lodash';
import * as PropTypes from 'prop-types';
import { ContentBox } from '../../../../shared/content-box/ContentBox';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';
import { RuleTypeLabelPipe } from '../../../../shared/pipes/RuleTypeLabelPipe';
import { getRuleCategoryFriendlyName } from '../../../../services/RuleCategory';
import { StatTile } from '../common/StatTile';
import { MAX_COMPARISON_PLAYER_LIMIT } from '../simulation.model';
import '../../../../shared/table/AlertsTable.scss';

export const formatDifference = difference =>
    difference > 0
        ? `+${difference}`
        : difference < 0
        ? `−${-difference}`
        : '0';

const differenceClass = difference =>
    difference > 0 ? 'positive' : difference < 0 ? 'negative' : '';

/* The name of a scenario in the catalog, or the words of its id for one that the catalog does not have (or does not have yet). */
export const scenarioLabel = (scenariosById, scenarioId) =>
    _.get(scenariosById, [scenarioId, 'name']) ||
    _.startCase(_.toLower(scenarioId));

/* The name of a player in one of the runs, which opens the page of the player when there is one to open: the id is null for a run that has no player of this number. */
const PlayerName = ({ side, player, canView, onView }) => {
    const username = player[`${side}Username`];

    return canView && onView && player[`${side}PlayerId`] ? (
        <button
            type={'button'}
            className={'sim-link sim-mono'}
            onClick={() => onView(side, player)}
        >
            {username}
        </button>
    ) : (
        <span className={'sim-mono'}>{username}</span>
    );
};

const RuleTags = ({ ruleTypes, empty }) =>
    _.isEmpty(ruleTypes) ? (
        <span className={'sim-muted'}>{empty || 'None'}</span>
    ) : (
        _.map(_.sortBy(ruleTypes), ruleType => (
            <span key={ruleType} className={'sim-tag'}>
                {RuleTypeLabelPipe(ruleType)}
            </span>
        ))
    );

/* Two runs of the same seed side by side: the alerts of each by rule, and the players whose outcome changed. */
export class ComparisonResult extends Component {
    static propTypes = {
        comparison: PropTypes.object,
        // The scenarios of the catalog by id, for their names
        scenariosById: PropTypes.object,
        // Asks for more of the players whose outcome changed
        onShowMorePlayers: PropTypes.func,
        loadingMore: PropTypes.bool,
        // Which of the two runs have a brand that can be selected, so that the page of a player in it can be opened
        canViewPlayers: PropTypes.shape({
            baseline: PropTypes.bool,
            variant: PropTypes.bool
        }),
        // (side, player): opens the page of the player of the baseline or of the variant
        onViewPlayer: PropTypes.func
    };

    renderByRule(comparison) {
        const rows = _.orderBy(
            comparison.byRuleType,
            [rule => Math.abs(rule.openDifference), 'ruleType'],
            ['desc', 'asc']
        );

        return (
            <div className={'table sim-padded'}>
                <table>
                    <tbody>
                        <tr className="headers">
                            <th>Rule</th>
                            <th>Category</th>
                            <th className={'align-right'}>Open · baseline</th>
                            <th className={'align-right'}>Open · variant</th>
                            <th className={'align-right'}>Difference</th>
                            <th className={'align-right'}>Raised · baseline</th>
                            <th className={'align-right'}>Raised · variant</th>
                        </tr>
                        {_.isEmpty(rows) ? (
                            <tr className="data">
                                <td colSpan={7} className={'sim-muted'}>
                                    Neither run raised any alert.
                                </td>
                            </tr>
                        ) : (
                            rows.map(rule => (
                                <tr key={rule.ruleType} className="data">
                                    <td>{RuleTypeLabelPipe(rule.ruleType)}</td>
                                    <td>
                                        {getRuleCategoryFriendlyName(
                                            rule.category
                                        ) || rule.category}
                                    </td>
                                    <td className={'align-right'}>
                                        {rule.baseline.open}
                                    </td>
                                    <td className={'align-right'}>
                                        {rule.variant.open}
                                    </td>
                                    <td
                                        className={
                                            'align-right sim-delta ' +
                                            differenceClass(rule.openDifference)
                                        }
                                    >
                                        {formatDifference(rule.openDifference)}
                                    </td>
                                    <td className={'align-right'}>
                                        {rule.baseline.total}
                                    </td>
                                    <td className={'align-right'}>
                                        {rule.variant.total}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    renderPlayers(comparison) {
        const {
            onShowMorePlayers,
            loadingMore,
            canViewPlayers,
            onViewPlayer,
            scenariosById
        } = this.props;
        const players = comparison.changedPlayers || [];
        const canShowMore =
            players.length < comparison.playersChanged &&
            players.length < MAX_COMPARISON_PLAYER_LIMIT;

        return (
            <div>
                <div className={'table sim-padded'}>
                    <table>
                        <tbody>
                            <tr className="headers">
                                <th>Player</th>
                                <th>Scenario</th>
                                <th>Alerts gained</th>
                                <th>Alerts lost</th>
                                <th>Open · baseline</th>
                                <th>Open · variant</th>
                            </tr>
                            {_.isEmpty(players) ? (
                                <tr className="data">
                                    <td colSpan={6} className={'sim-muted'}>
                                        No player ended with other alerts.
                                    </td>
                                </tr>
                            ) : (
                                players.map(player => (
                                    <tr
                                        key={`${player.scenarioId}-${player.playerNumber}`}
                                        className="data"
                                    >
                                        <td>
                                            <div>
                                                Player {player.playerNumber}
                                            </div>
                                            {['baseline', 'variant'].map(
                                                side => (
                                                    <div
                                                        key={side}
                                                        className={'sim-muted'}
                                                    >
                                                        {side}{' '}
                                                        <PlayerName
                                                            side={side}
                                                            player={player}
                                                            canView={_.get(
                                                                canViewPlayers,
                                                                side
                                                            )}
                                                            onView={
                                                                onViewPlayer
                                                            }
                                                        />
                                                    </div>
                                                )
                                            )}
                                        </td>
                                        <td>
                                            {scenarioLabel(
                                                scenariosById,
                                                player.scenarioId
                                            )}
                                        </td>
                                        <td>
                                            <RuleTags
                                                ruleTypes={player.gained}
                                            />
                                        </td>
                                        <td>
                                            <RuleTags ruleTypes={player.lost} />
                                        </td>
                                        <td>
                                            <RuleTags
                                                ruleTypes={player.baselineOpen}
                                                empty={'No open alert'}
                                            />
                                        </td>
                                        <td>
                                            <RuleTags
                                                ruleTypes={player.variantOpen}
                                                empty={'No open alert'}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={'sim-muted'} style={{ marginTop: 10 }}>
                    Showing {players.length} of {comparison.playersChanged}{' '}
                    players.
                    {canShowMore && (
                        <TransparentButton
                            disabled={loadingMore}
                            onClick={onShowMorePlayers}
                        >
                            Show more
                        </TransparentButton>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { comparison } = this.props;

        if (!comparison) {
            return null;
        }
        const openDifference =
            comparison.variant.open - comparison.baseline.open;
        const totalDifference =
            comparison.variant.total - comparison.baseline.total;

        return (
            <div className={'sim-comparison'}>
                <div className={'sim-stats'}>
                    <StatTile label={'Players'} value={comparison.players} />
                    <StatTile
                        label={'Players with another outcome'}
                        value={comparison.playersChanged}
                        detail={
                            comparison.players
                                ? `${Math.round(
                                      (comparison.playersChanged /
                                          comparison.players) *
                                          100
                                  )}% of the players`
                                : null
                        }
                    />
                    <StatTile
                        label={'Open alerts'}
                        value={`${comparison.baseline.open} → ${comparison.variant.open}`}
                        detail={`${formatDifference(
                            openDifference
                        )} with the change`}
                    />
                    <StatTile
                        label={'Alerts raised'}
                        value={`${comparison.baseline.total} → ${comparison.variant.total}`}
                        detail={`${formatDifference(
                            totalDifference
                        )} with the change`}
                    />
                </div>

                <div className={'sim-section'}>
                    <ContentBox
                        title={'Alerts by rule'}
                        tooltipMessage={
                            'Open alerts are the outcome of a player: the alerts that are still open when the run ends. The difference is the open alerts with the change less the open alerts without it.'
                        }
                        content={this.renderByRule(comparison)}
                    />
                </div>
                <div className={'sim-section'}>
                    <ContentBox
                        title={'Players whose outcome changed'}
                        content={this.renderPlayers(comparison)}
                    />
                </div>
            </div>
        );
    }
}
